import { getDcApiBase } from "./config";
import { lawvoFetch } from "./httpClient";
import { clearAllAuthStorage, getDcAccessToken, getLawvoAccessToken, getLawvoUser, loadDcState, loadLawvoState, saveDcState, saveLawvoState } from "./lawvoStorage";
export interface AuthUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}
export interface LawvoPersistedUser {
  id?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}
export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}
export interface Auth2FAResponse {
  requires2FA: true;
  type: string;
  tempAccessToken: string;
}
export interface DcSessionResponse {
  jwt: string;
  user: Record<string, unknown>;
}
function normalizeAuthUser(raw: Record<string, unknown>): AuthUser {
  return {
    id: Number(raw.id),
    email: String(raw.email || ""),
    firstName: raw.firstName ? String(raw.firstName) : undefined,
    lastName: raw.lastName ? String(raw.lastName) : undefined,
    role: raw.role ? String(raw.role) : undefined
  };
}
export async function loginWithCredentials(email: string, password: string): Promise<AuthResponse | Auth2FAResponse> {
  const response = await lawvoFetch("/authentication", {
    method: "POST",
    skipAuthRedirect: true,
    body: JSON.stringify({
      strategy: "local",
      email,
      password
    })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Login failed");
  }
  return response.json();
}
export async function reAuthWithToken(token: string): Promise<AuthResponse> {
  const response = await lawvoFetch("/authentication", {
    method: "POST",
    skipAuthRedirect: true,
    body: JSON.stringify({
      strategy: "jwt",
      accessToken: token
    })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Authentication failed");
  }
  return response.json();
}

/**
 * Exchange Lawvo user identity for a Strapi JWT — same as lawvo-client
 * getDcUserApi / doc-center getUserEndpoint (`POST /custom/get-user/`).
 */
export async function fetchDcSession(user: AuthUser): Promise<DcSessionResponse> {
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const response = await fetch(`${getDcApiBase()}/custom/get-user/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      id: user.id,
      userID: user.id,
      firstName,
      lastName,
      email: user.email,
      userName: `${firstName} ${lastName}`.trim() || user.email,
      role: user.role || "super-admin"
    })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || `Failed to obtain Document Center session (${response.status})`);
  }
  return response.json();
}
export async function establishDcSession(user: AuthUser, options?: {
  forceRefresh?: boolean;
}): Promise<DcSessionResponse> {
  const existing = getDcAccessToken();
  if (existing && !options?.forceRefresh) {
    return {
      jwt: existing,
      user: loadDcState()?.user || {}
    };
  }
  const session = await fetchDcSession(user);
  saveDcState({
    user: session.user,
    jwt: session.jwt
  });
  return session;
}
export function persistLawvoSession(response: AuthResponse): void {
  saveLawvoState({
    accessToken: response.accessToken,
    user: response.user
  });
}
export function getStoredToken(): string | null {
  const token = getLawvoAccessToken();
  return token || null;
}
export function getStoredUser(): AuthUser | null {
  const user = getLawvoUser();
  if (!user?.email) return null;
  return normalizeAuthUser(user as Record<string, unknown>);
}
export function setStoredToken(token: string): void {
  const state = loadLawvoState() || {};
  saveLawvoState({
    ...state,
    accessToken: token
  });
}
export function setStoredUser(user: AuthUser): void {
  const state = loadLawvoState() || {};
  saveLawvoState({
    ...state,
    user
  });
}
export function clearStoredAuth(): void {
  clearAllAuthStorage();
}